import { useState, useEffect } from 'react';
import { Key, Plus, Download, Ban, Loader2, AlertCircle, Shield, Smartphone } from 'lucide-react';
import api from '../../services/api';
import ConfirmDialog from '../common/ConfirmDialog';
import { useToast } from '../common/ToastContainer';
import { formatDate } from '../../utils/formatDate';
import { DataTable, type Column } from '../common/DataTable';
import { useTenantStore } from '../../stores/tenantStore';

interface Certificate {
  id: string;
  device_id: string;
  serial_number: string;
  common_name: string;
  issued_at: string;
  expires_at: string;
  revoked_at: string | null;
  status: 'active' | 'revoked' | 'expired';
  device_name: string | null;   // friendly name from app_devices (e.g. "MBP-01")
  owner_email: string | null;   // device owner email from app_devices
}

interface Props {
  edgeId: string;
  edgeGuid: string;
}

export default function IKEv2CertificateManager({ edgeId: _edgeId, edgeGuid }: Props) {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [selectedCert, setSelectedCert] = useState<Certificate | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [issueForm, setIssueForm] = useState({ email: '', deviceId: '' });
  const [issuedResult, setIssuedResult] = useState<{ cert: Certificate; privateKey: string } | null>(null);
  const toast = useToast();
  // Re-fetch when the SuperAdmin switches tenant via the topbar. Without this,
  // the initial mount can race the tenant-store hydration and fetch with the
  // SuperAdmin's own tenantId, getting an empty list that never refreshes.
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);

  const fetchCertificates = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/v2/wireguard/apps/ikev2/${edgeGuid}/certificates`);
      // api client's response interceptor already unwraps to the HTTP body,
      // so `res` is { success, data: { certificates } } — not the axios response.
      setCertificates(res.data?.certificates || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, [edgeGuid, selectedTenantId]);

  const handleIssueCert = async () => {
    if (!issueForm.email.trim()) {
      toast.error('Validation Error', 'Email is required');
      return;
    }
    setIssuing(true);
    try {
      const res = await api.post(`/api/v2/wireguard/apps/ikev2/${edgeGuid}/certificates`, {
        email: issueForm.email,
        deviceId: issueForm.deviceId || 'manual',
      });
      const data = res.data;
      setIssuedResult({ cert: data.certificate, privateKey: data.privateKey });
      toast.success('Certificate Issued', `Certificate for ${issueForm.email} has been issued.`);
      fetchCertificates();
    } catch (err: any) {
      toast.error('Issue Failed', err.response?.data?.message || 'Failed to issue certificate');
    } finally {
      setIssuing(false);
    }
  };

  const handleRevoke = async () => {
    if (!selectedCert) return;
    setRevoking(true);
    try {
      await api.post(`/api/v2/wireguard/apps/ikev2/certificates/${selectedCert.id}/revoke`, {
        reason: 'manual_revocation',
      });
      toast.success('Certificate Revoked', `Certificate for ${selectedCert.common_name} has been revoked.`);
      setShowRevokeConfirm(false);
      setSelectedCert(null);
      fetchCertificates();
    } catch (err: any) {
      toast.error('Revoke Failed', err.response?.data?.message || 'Failed to revoke certificate');
    } finally {
      setRevoking(false);
    }
  };

  const handleDownloadP12 = async (cert: Certificate) => {
    try {
      const res = await api.get(`/api/v2/wireguard/apps/ikev2/certificates/${cert.id}/pkcs12?password=securelink`, {
        responseType: 'blob',
      });
      const blob = new Blob([res as unknown as BlobPart], { type: 'application/x-pkcs12' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `securelink-vpn-${cert.common_name.replace(/[^a-z0-9]/gi, '-')}.p12`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error('Download Failed', err.response?.data?.message || 'Failed to download certificate');
    }
  };

  const handleDownloadProfile = async (cert: Certificate) => {
    try {
      const res = await api.get(`/api/v2/wireguard/apps/ikev2/certificates/${cert.id}/profile/apple?password=securelink`, {
        responseType: 'blob',
      });
      const blob = new Blob([res as unknown as BlobPart], { type: 'application/x-apple-aspen-config' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `securelink-vpn-${cert.common_name.replace(/[^a-z0-9]/gi, '-')}.mobileconfig`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error('Download Failed', err.response?.data?.message || 'Failed to download profile');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>;
      case 'revoked':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Revoked</span>;
      case 'expired':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Expired</span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  // Cert CN is the device GUID for current certs (email for legacy pre-fix certs).
  const isEmailCN = (cn: string) => /@/.test(cn);

  const columns: Column<Certificate>[] = [
    {
      key: 'device_name',
      label: 'Device / Identity',
      sortable: true,
      render: (cert) => {
        const legacy = !cert.device_name && isEmailCN(cert.common_name);
        const primary = cert.device_name ?? cert.common_name;
        return (
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
              {primary}
              {!cert.device_name && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                  {legacy ? 'legacy (email CN)' : 'no device match'}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">
              {cert.owner_email ? `${cert.owner_email} · ` : ''}SN {cert.serial_number.substring(0, 12)}…
            </div>
          </div>
        );
      },
    },
    { key: 'status', label: 'Status', sortable: true, render: (cert) => getStatusBadge(cert.status) },
    { key: 'issued_at', label: 'Issued', sortable: true, render: (cert) => <span className="text-sm text-gray-500">{formatDate(cert.issued_at)}</span> },
    { key: 'expires_at', label: 'Expires', sortable: true, render: (cert) => <span className="text-sm text-gray-500">{formatDate(cert.expires_at)}</span> },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-right',
      render: (cert) => cert.status !== 'active' ? null : (
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => handleDownloadP12(cert)}
            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
            title="Download .p12 certificate"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDownloadProfile(cert)}
            className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded"
            title="Download Apple profile (.mobileconfig)"
          >
            <Smartphone className="w-4 h-4" />
          </button>
          <button
            onClick={() => { setSelectedCert(cert); setShowRevokeConfirm(true); }}
            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
            title="Revoke certificate"
          >
            <Ban className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Shield className="w-5 h-5 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">IKEv2 Client Certificates</h3>
        </div>
        <button
          onClick={() => {
            setShowIssueModal(true);
            setIssueForm({ email: '', deviceId: '' });
            setIssuedResult(null);
          }}
          className="btn-primary flex items-center text-sm"
        >
          <Plus className="w-4 h-4 mr-1" />
          Issue Certificate
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      <DataTable
        columns={columns}
        data={certificates}
        loading={loading}
        keyExtractor={(cert) => cert.id}
        searchPlaceholder="Search by device, email, or serial…"
        emptyMessage="No active certificates"
      />

      {/* Issue Certificate Modal */}
      {showIssueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <Key className="w-6 h-6 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {issuedResult ? 'Certificate Issued' : 'Issue IKEv2 Certificate'}
                </h3>
              </div>

              {issuedResult ? (
                <div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-green-800">
                      Certificate issued for <strong>{issuedResult.cert.common_name}</strong>
                    </p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-amber-800">
                      <strong>Important:</strong> The private key is shown below. Save it now — it cannot be retrieved later.
                    </p>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Private Key</label>
                    <textarea
                      readOnly
                      value={issuedResult.privateKey}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-xs font-mono"
                      rows={6}
                    />
                  </div>
                  <div className="flex items-center justify-end space-x-3">
                    <button
                      onClick={() => handleDownloadP12(issuedResult.cert)}
                      className="btn-secondary flex items-center text-sm"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download .p12
                    </button>
                    <button
                      onClick={() => {
                        setShowIssueModal(false);
                        setIssuedResult(null);
                      }}
                      className="btn-primary text-sm"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email / Identity</label>
                      <input
                        type="email"
                        value={issueForm.email}
                        onChange={(e) => setIssueForm({ ...issueForm, email: e.target.value })}
                        placeholder="user@example.com"
                        className="input-base w-full"
                      />
                      <p className="mt-1 text-xs text-gray-500">Used as the certificate identity (CN) for authentication</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Device ID (optional)</label>
                      <input
                        type="text"
                        value={issueForm.deviceId}
                        onChange={(e) => setIssueForm({ ...issueForm, deviceId: e.target.value })}
                        placeholder="auto-assigned"
                        className="input-base w-full"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex items-center justify-end space-x-3">
                    <button
                      onClick={() => setShowIssueModal(false)}
                      disabled={issuing}
                      className="btn-secondary text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleIssueCert}
                      disabled={issuing || !issueForm.email.trim()}
                      className="btn-primary flex items-center text-sm"
                    >
                      {issuing && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                      Issue Certificate
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Revoke Confirmation */}
      <ConfirmDialog
        isOpen={showRevokeConfirm}
        onClose={() => {
          setShowRevokeConfirm(false);
          setSelectedCert(null);
        }}
        onConfirm={handleRevoke}
        title="Revoke Certificate"
        message={`Are you sure you want to revoke the certificate for ${selectedCert?.common_name}? The client will be disconnected and unable to reconnect.`}
        confirmText="Revoke"
        variant="danger"
        isLoading={revoking}
      />
    </div>
  );
}
